import { useMemo, useState } from 'react'
import { ChevronRight, KeyRound, Search, X } from 'lucide-react'
import type { BrokerProviderDto } from '@apna/shared'
import { cn } from '@/lib/utils'

/**
 * Broker picker, matching trading-journal's `BrokerSelectModal`.
 *
 * The journal's version lists 15 brokers under "Popular" and "More auto-sync".
 * ApnaAlgo has one provider today, so the sections are driven by the data
 * rather than by a hardcoded popularity list — with one provider the search
 * box and the second section simply do not render.
 *
 * Providers are grouped by `adapterFamily` because that is what actually
 * differs for the user: every XTS deployment behaves identically regardless of
 * which broker hosts it, and a self-hosted gateway means two XTS brokers are
 * two provider rows, not one.
 */

interface Props {
  open: boolean
  providers: BrokerProviderDto[]
  onSelect: (provider: BrokerProviderDto) => void
  onClose: () => void
}

export function BrokerSelectModal({ open, providers, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return providers
    return providers.filter(
      (p) => p.displayName.toLowerCase().includes(q) || p.key.toLowerCase().includes(q),
    )
  }, [providers, query])

  if (!open) return null

  const chosen = providers.find((p) => p.key === selected)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        className="absolute inset-0 cursor-default bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
      />

      <div className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border bg-card shadow-lg">
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-foreground">Add Broker Setup</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">Select your broker to continue.</p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors hover:bg-accent"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {providers.length > 4 && (
            <div className="relative mb-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search broker..."
                className="h-10 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm outline-none focus:border-ring"
              />
            </div>
          )}

          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Available brokers
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground/80">
            Connect once — orders route through your own credentials.
          </p>

          <div className="mt-3 space-y-2">
            {filtered.map((p) => (
              <button
                key={p.key}
                onClick={() => setSelected(p.key)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
                  selected === p.key ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                )}
                data-testid={`broker-${p.key}`}
              >
                <div
                  className="inline-flex shrink-0 items-center justify-center rounded-lg border bg-background"
                  style={{ width: 36, height: 36 }}
                >
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{p.displayName}</p>
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {/*
                      Surfaced because it decides what the next screen asks
                      for: XTS needs TWO credential pairs, and a user who
                      expects one form is a user who stops halfway.
                    */}
                    {p.credentialRoles.length > 1
                      ? `${p.credentialRoles.length} credential pairs — orders and market data`
                      : 'One credential pair'}
                    {p.marketDataCap ? ` · ${p.marketDataCap} instrument feed cap` : ''}
                  </p>
                </div>

                <ChevronRight
                  className={cn(
                    'h-4 w-4 shrink-0',
                    selected === p.key ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
              </button>
            ))}

            {filtered.length === 0 && (
              <p className="py-8 text-center text-xs text-muted-foreground">
                No broker matches “{query}”.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-5 py-4">
          <button
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-lg border px-4 text-sm font-medium transition-colors hover:bg-accent"
          >
            Cancel
          </button>
          <button
            disabled={!chosen}
            onClick={() => chosen && onSelect(chosen)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-40"
            data-testid="broker-continue"
          >
            Continue <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
